import { Heart, Users, Target } from 'lucide-react';

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative bg-slate-800 overflow-hidden pt-40 md:pt-[calc(12rem+10vh)] pb-12 md:pb-16" data-testid="about-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700" />
        
        <div className="relative container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 animate-fade-in-up"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              <span className="text-purple-200 italic">About Aim Hire</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animation-delay-100">
              Connecting proven professionals with employers who value depth, stability, and performance.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="our-story">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                Our Story
              </p>
              <h2 
                className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Experience Matters
              </h2>
              <div className="space-y-4 text-base text-slate-600 leading-relaxed">
                <p>
                  Aim Hire was founded on a simple belief: <strong className="text-slate-900">experience matters.</strong>
                </p>
                <p>
                  In today's hiring landscape, capable professionals are often filtered out before 
                  their value is fully seen. At the same time, organizations are searching for 
                  reliable, mature talent who can contribute immediately and long-term.
                </p>
                <p>
                  We exist to bridge that gap—connecting proven professionals with employers who 
                  value depth, stability, and performance.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100">
                <img 
                  src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80"
                  alt="Professional team collaboration"
                  className="w-full h-full object-cover"
                  data-testid="about-image"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-100 rounded-xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-20 bg-slate-900" data-testid="mission-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
              Our Mission
            </p>
            <h2 
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Meaningful Work, Lasting Impact
            </h2>
            <p className="text-base text-slate-300 leading-relaxed">
              To connect experienced professionals with meaningful work—strengthening organizations, 
              families, and communities in the process.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
              What We Stand For
            </p>
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Our Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white p-6 rounded-xl border border-slate-100 card-hover" data-testid="value-1">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-slate-700" />
              </div>
              <h3 
                className="text-lg font-semibold text-slate-900 mb-2"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Respect for Experience
              </h3>
              <p className="text-sm text-slate-600">
                Experience is an asset. Professionals should be evaluated on ability and 
                contribution—not assumptions.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-slate-100 card-hover" data-testid="value-2">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-slate-700" />
              </div>
              <h3 
                className="text-lg font-semibold text-slate-900 mb-2"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Community Impact
              </h3>
              <p className="text-sm text-slate-600">
                When experienced professionals return to meaningful work, organizations grow 
                stronger and families regain stability.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-slate-100 card-hover" data-testid="value-3">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-slate-700" />
              </div>
              <h3 
                className="text-lg font-semibold text-slate-900 mb-2"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Intentional Placement
              </h3>
              <p className="text-sm text-slate-600">
                We combine human vetting, thoughtful matching, and direct employer relationships 
                to ensure every introduction is purposeful.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="team-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
              Our Team
            </p>
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900 mb-3"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Led by Experience
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Aim Hire is led by professionals who understand both the hiring landscape 
              and the human side of career transition.
            </p>
          </div>

          {/* Executive Director - Top Row */}
          <div className="flex justify-center mb-8">
            <div className="text-center bg-slate-50 rounded-xl px-10 py-6 border border-slate-100">
              <h3 
                className="text-xl font-semibold text-slate-900 mb-1"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Rabbi Keevy Fried
              </h3>
              <p className="text-sm text-slate-600 font-medium">Executive Director</p>
            </div>
          </div>

          {/* Team Members - Bottom Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10">
            <div className="text-center bg-slate-50 rounded-xl px-6 py-5 border border-slate-100">
              <h3 
                className="text-base font-semibold text-slate-900 mb-1"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Sheyna Barkhordar
              </h3>
              <p className="text-xs text-slate-600">Assistant Director,<br />Placement Coordinator</p>
            </div>
            
            <div className="text-center bg-slate-50 rounded-xl px-6 py-5 border border-slate-100">
              <h3 
                className="text-base font-semibold text-slate-900 mb-1"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Tsirel Stern
              </h3>
              <p className="text-xs text-slate-600">Operations Manager</p>
            </div>
            
            <div className="text-center bg-slate-50 rounded-xl px-6 py-5 border border-slate-100">
              <h3 
                className="text-base font-semibold text-slate-900 mb-1"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Renana Witty
              </h3>
              <p className="text-xs text-slate-600">Intake Coordinator</p>
            </div>
          </div>

          {/* Team Closing Statement */}
          <p className="text-center text-sm text-slate-600 max-w-xl mx-auto">
            Together, we work closely with employers, candidates, and partners to ensure 
            every placement is positioned for long-term success.
          </p>
        </div>
      </section>
    </div>
  );
}
